import React, { useState } from 'react';
import { Button, Input, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    DragOutlined,
    EditOutlined,
    PlusOutlined
} from '@ant-design/icons';

import showDeleteConfirm from '../../../../components/Confirmation-modal';

import CriterionStats from './CriterionStats';

const { Text } = Typography;

export default function CriteriaList({
    criteriaList = [],
    onChange,
    placeholder,
    allowReorder = true,
    // Stats props
    showStats = false,
    criteriaStats = [],
    statsLoading = false,
    totalEvaluatedTrades = 0
}) {
    // Helper to find stats for a criterion
    const getStatsForCriterion = (criterionId) => {
        if (!showStats || !criteriaStats.length) return null;
        return criteriaStats.find((s) => s.criterionId?.toString() === criterionId?.toString());
    };

    const [newCriterionText, setNewCriterionText] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);

    const handleAddCriterion = () => {
        if (!newCriterionText.trim()) return;

        const newCriterion = {
            text: newCriterionText.trim(),
            orderIndex: criteriaList.length
        };

        onChange([...criteriaList, newCriterion]);
        setNewCriterionText('');
    };

    const handleDeleteCriterion = (index, text) => {
        showDeleteConfirm(
            (confirmed) => {
                if (confirmed) {
                    const updated = criteriaList.filter((_, i) => i !== index);
                    // Update order indices
                    const reindexed = updated.map((item, idx) => ({
                        ...item,
                        orderIndex: idx
                    }));
                    onChange(reindexed);
                    setSelectedIds(selectedIds.filter((selectedId) => selectedId !== index));
                }
            },
            text,
            'Delete this criterion?'
        );
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;

        showDeleteConfirm(
            (confirmed) => {
                if (confirmed) {
                    const updated = criteriaList.filter((_, index) => !selectedIds.includes(index));
                    // Update order indices
                    const reindexed = updated.map((item, idx) => ({
                        ...item,
                        orderIndex: idx
                    }));
                    onChange(reindexed);
                    setSelectedIds([]);
                }
            },
            `${selectedIds.length} criteria`,
            'Delete selected criteria?'
        );
    };

    const handleStartEdit = (index, text) => {
        setEditingId(index);
        setEditingText(text);
    };

    const handleSaveEdit = (index) => {
        if (!editingText.trim()) {
            setEditingId(null);
            setEditingText('');
            return;
        }

        const updated = criteriaList.map((item, idx) =>
            idx === index ? { ...item, text: editingText.trim() } : item
        );
        onChange(updated);
        setEditingId(null);
        setEditingText('');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingText('');
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(criteriaList);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update orderIndex
        const reordered = items.map((item, index) => ({
            ...item,
            orderIndex: index
        }));

        onChange(reordered);
    };

    const handleToggleSelect = (index) => {
        setSelectedIds((prev) =>
            prev.includes(index)
                ? prev.filter((selectedId) => selectedId !== index)
                : [...prev, index]
        );
    };

    return (
        <div
            className={`criteria-list ${showStats ? 'criteria-list--with-stats' : ''}`}
            style={{ textAlign: 'left' }}
        >
            {/* Column headers when showing stats */}
            {showStats && criteriaList.length > 0 && (
                <div className="criteria-list-header">
                    <div className="header-spacer" />
                    <div className="header-text">Criterion</div>
                    <div className="header-stats">
                        <span>Rate</span>
                        <span>Net P/L</span>
                        <span>PF</span>
                        <span>Win%</span>
                    </div>
                    <div className="header-actions" />
                </div>
            )}

            {/* Bulk actions */}
            {criteriaList.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <Space>
                        {selectedIds.length > 0 && (
                            <Button
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={handleBulkDelete}
                            >
                                Delete ({selectedIds.length})
                            </Button>
                        )}
                    </Space>
                </div>
            )}

            {/* Criteria list */}
            {criteriaList.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
                    <Text type="secondary">No criteria added yet. Add your first criterion.</Text>
                </div>
            ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="criteria-list">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {criteriaList.map((item, index) => {
                                    const itemKey = item._id?.toString() || `item-${index}`;
                                    return (
                                        <Draggable
                                            key={itemKey}
                                            draggableId={itemKey}
                                            index={index}
                                            isDragDisabled={!allowReorder}
                                        >
                                            {(dragProvided, snapshot) => (
                                                <div
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    style={{
                                                        ...dragProvided.draggableProps.style,
                                                        background: snapshot.isDragging
                                                            ? '#f0f0f0'
                                                            : 'white',
                                                        border: '1px solid #d9d9d9',
                                                        borderRadius: '4px',
                                                        marginBottom: '8px',
                                                        padding: '8px 12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        lineHeight: '1'
                                                    }}
                                                >
                                                    {allowReorder && (
                                                        <div {...dragProvided.dragHandleProps}>
                                                            <DragOutlined
                                                                style={{ cursor: 'grab' }}
                                                            />
                                                        </div>
                                                    )}
                                                    <div
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            border: '1px solid #d9d9d9',
                                                            borderRadius: '2px',
                                                            background: selectedIds.includes(index)
                                                                ? '#1890ff'
                                                                : 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => handleToggleSelect(index)}
                                                    >
                                                        {selectedIds.includes(index) && (
                                                            <CheckOutlined
                                                                style={{
                                                                    color: 'white',
                                                                    fontSize: '12px'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    {editingId === index ? (
                                                        <Space.Compact style={{ flex: 1 }}>
                                                            <Input
                                                                value={editingText}
                                                                onChange={(e) =>
                                                                    setEditingText(e.target.value)
                                                                }
                                                                onPressEnter={() =>
                                                                    handleSaveEdit(index)
                                                                }
                                                                autoFocus
                                                                style={{ flex: 1 }}
                                                            />
                                                            <Button
                                                                type="primary"
                                                                icon={<CheckOutlined />}
                                                                onClick={() =>
                                                                    handleSaveEdit(index)
                                                                }
                                                            />
                                                            <Button
                                                                icon={<CloseOutlined />}
                                                                onClick={handleCancelEdit}
                                                            />
                                                        </Space.Compact>
                                                    ) : (
                                                        <>
                                                            <Text
                                                                className="criterion-text"
                                                                style={{ flex: 1 }}
                                                            >
                                                                {item.text}
                                                            </Text>

                                                            {/* Stats display */}
                                                            {showStats && (
                                                                <CriterionStats
                                                                    stats={{
                                                                        ...getStatsForCriterion(
                                                                            item._id
                                                                        ),
                                                                        totalEvaluatedTrades
                                                                    }}
                                                                    loading={statsLoading}
                                                                />
                                                            )}

                                                            <Space className="criterion-actions">
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() =>
                                                                        handleStartEdit(
                                                                            index,
                                                                            item.text
                                                                        )
                                                                    }
                                                                />
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    size="small"
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={() =>
                                                                        handleDeleteCriterion(
                                                                            index,
                                                                            item.text
                                                                        )
                                                                    }
                                                                />
                                                            </Space>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            {/* Add new criterion */}
            <Space.Compact style={{ width: '100%', marginTop: 16 }}>
                <Input
                    placeholder={placeholder || 'Add new criterion...'}
                    value={newCriterionText}
                    onChange={(e) => setNewCriterionText(e.target.value)}
                    onPressEnter={handleAddCriterion}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCriterion}>
                    Add
                </Button>
            </Space.Compact>
        </div>
    );
}

CriteriaList.propTypes = {
    criteriaList: PropTypes.arrayOf(
        PropTypes.shape({
            _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            text: PropTypes.string.isRequired,
            orderIndex: PropTypes.number.isRequired
        })
    ),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    allowReorder: PropTypes.bool,
    // Stats props
    showStats: PropTypes.bool,
    criteriaStats: PropTypes.arrayOf(
        PropTypes.shape({
            criterionId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            presentRate: PropTypes.number,
            netPnL: PropTypes.number,
            profitFactor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            winRate: PropTypes.number,
            totalTrades: PropTypes.number
        })
    ),
    statsLoading: PropTypes.bool,
    totalEvaluatedTrades: PropTypes.number
};
